import {
  Badge,
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Typography,
  useMediaQuery
} from "@mui/material";
// assets
import { Clock, Message, Notification } from "iconsax-react";
import Transitions, { PopupTransition } from "components/@extended/Transitions";
import { getNotifications, setNotificationRead } from "store/reducers/users";
import { useEffect, useRef, useState } from "react";

import Avatar from "components/@extended/Avatar";
import IconButton from "components/@extended/IconButton";
// project-imports
import MainCard from "components/MainCard";
import { ThemeMode } from "config";
import dayjs from "dayjs";
import { dispatch } from "store";
import { get } from "lodash";
import { useSelector } from "store";
// material-ui
import { useTheme } from "@mui/material/styles";
import useUserAuthInfo from "hooks/useAuthInfo";

const actionSX = {
  mt: "6px",
  ml: 1,
  top: "auto",
  right: "auto",
  alignSelf: "flex-start",

  transform: "none"
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

const NotificationPage = () => {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));
  const { token, clientid } = useUserAuthInfo();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };
  const notifications = useSelector((state) => state.usersmanagement.notifications);

  const data = get(notifications, "data.content", []);
  const unreadcount = data.filter((notification) => notification.read === false).length;

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleDialogOpen = (notification) => {
    setSelectedNotification(notification);
    setNotificationRead(token, clientid, notification.id).then(() => {
      dispatch(getNotifications(token, clientid));
    });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedNotification(null);
  };

  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? "secondary.200" : "secondary.200";
  const iconBackColor = theme.palette.mode === ThemeMode.DARK ? "background.default" : "secondary.100";

  useEffect(() => {
    dispatch(getNotifications(token, clientid));
  }, []);

  const recentNotifications = data
    .filter((notification) => !notification.read)
    .sort((a, b) => new Date(b.createdat) - new Date(a.createdat))
    .slice(0, 3);

  return (
    <Box sx={{ flexShrink: 0, ml: 0.5 }}>
      <IconButton
        color="secondary"
        variant="light"
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? "profile-grow" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        size="large"
        sx={{
          color: "secondary.main",
          bgcolor: open ? iconBackColorOpen : iconBackColor,
          p: 1,
          animation: unreadcount > 0 ? "pulse 1.5s infinite" : "none",
          "@keyframes pulse": {
            "0%": {
              transform: "scale(1)"
            },
            "50%": {
              transform: "scale(1.1)"
            },
            "100%": {
              transform: "scale(1)"
            }
          }
        }}
      >
        <Badge badgeContent={unreadcount} color="error" sx={{ "& .MuiBadge-badge": { top: 2, right: 4 } }}>
          <Notification variant="Bold" />
        </Badge>
      </IconButton>
      <Popper
        placement={matchesXs ? "bottom" : "bottom-end"}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [matchesXs ? -5 : 0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={matchesXs ? "top" : "top-right"} sx={{ overflow: "hidden" }} in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                borderRadius: 1.5,
                width: "100%",
                minWidth: 285,
                maxWidth: 420,
                [theme.breakpoints.down("md")]: {
                  maxWidth: 285
                }
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h5">Notifications</Typography>
                    <Divider />
                  </Stack>
                  <List
                    component="nav"
                    sx={{
                      "& .MuiListItemButton-root": {
                        p: 1.5,
                        my: 1.5,
                        border: `1px solid ${theme.palette.divider}`,
                        "&:hover": {
                          bgcolor: "primary.lighter",
                          borderColor: theme.palette.primary.light
                        },
                        "& .MuiListItemSecondaryAction-root": { ...actionSX, position: "relative" }
                      }
                    }}
                  >
                    {recentNotifications && recentNotifications.length > 0 ? (
                      recentNotifications.map((notification) => (
                        <ListItemButton key={notification.id} onClick={() => handleDialogOpen(notification)}>
                          <ListItemAvatar>
                            <Avatar>
                              <Message variant="Bold" />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={<Typography variant="h6">{notification.subject}</Typography>}
                            secondary={dayjs(notification.createdat).format("HH:mm:ss")}
                          />
                        </ListItemButton>
                      ))
                    ) : (
                      <ListItem>
                        <ListItemText primary="No notifications received yet" />
                      </ListItem>
                    )}
                  </List>
                  <Stack direction="row" justifyContent="center">
                    <Link href="/notifications" variant="h6" color="primary">
                      View all
                    </Link>
                  </Stack>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>

      <Dialog keepMounted TransitionComponent={PopupTransition} maxWidth="xs" fullWidth open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar>
              <Notification variant="Bulk" />
            </Avatar>
            <Typography variant="h5">Notification</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedNotification && (
            <>
              <Stack spacing={1}>
                <Typography variant="h4">{selectedNotification.subject}</Typography>
                <Typography variant="body1">{selectedNotification.message}</Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Clock size={12} />
                  <Typography variant="caption" color="primary.dark">
                    {dayjs(selectedNotification.createdat).format("MMM D, YYYY @ h:mm A")}
                  </Typography>
                </Stack>
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="error">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationPage;
